/**
 * Consent gate for PostHog: the SDK is not initialised at all until the
 * visitor grants analytics consent.
 *
 * Why not "init opted-out, opt in later" (PostHog's own recommendation): an
 * opted-out `posthog.init` still contacts PostHog before any consent — measured
 * 2026-09-30 on a fresh browser profile it fetched `config.js`, POSTed `/flags`
 * (carrying a generated device/distinct id) and loaded the surveys,
 * dead-clicks, web-vitals and exception-autocapture scripts. Instructing the
 * visitor's browser to send identifiers to a third party is "gaining access"
 * under ePrivacy čl. 5 ods. 3 (EDPB Guidelines 2/2023 on the technical scope of
 * Art. 5(3)), transposed by § 109 ods. 8 zákona č. 452/2021 Z. z., and none of
 * it is strictly necessary for the service. So nothing PostHog-related may run
 * before an opt-in (#11843). Do not move `init` back to mount.
 */
export interface PostHogConsentClient {
  /** Initialise the SDK. Called at most once, and only after a grant. */
  init: () => void;
  /** Start capturing (called after init, on every grant). */
  optIn: () => void;
  /** Stop capturing and drop identifiers (called only if init ever ran). */
  optOut: () => void;
}

interface PostHogConsentGateOptions {
  /** false in development, so a local "accept" never reaches prod analytics. */
  allowOptIn: boolean;
  /** Defers the grant path (e.g. to browser idle) to keep init off the LCP path. */
  schedule: (task: () => void) => void;
  /**
   * Removes PostHog's browser-storage leftovers. Called on a refusal while the
   * SDK was never initialised in this page — i.e. data a previous page load
   * (or the pre-#11843 build, which initialised at mount) left behind.
   */
  clearLeftovers?: () => void;
}

interface PostHogConsentGate {
  update: (granted: boolean) => void;
  isInitialised: () => boolean;
}

export function createPostHogConsentGate(
  client: PostHogConsentClient,
  { allowOptIn, schedule, clearLeftovers }: PostHogConsentGateOptions
): PostHogConsentGate {
  let initialised = false;
  let latestGrant = false;

  const applyGrant = (): void => {
    // Consent may have been withdrawn between the grant and the deferred run.
    if (!latestGrant) return;
    if (!initialised) {
      initialised = true;
      client.init();
    }
    client.optIn();
  };

  return {
    update(granted: boolean): void {
      latestGrant = granted && allowOptIn;
      if (latestGrant) {
        schedule(applyGrant);
        return;
      }
      if (initialised) {
        client.optOut();
        return;
      }
      // Never initialised in this page → nothing to stop, but an earlier page
      // load may have stored PostHog identifiers that no SDK call will clear.
      // Only on a real refusal: a development-suppressed grant is not one.
      if (!granted) clearLeftovers?.();
    },
    isInitialised: () => initialised,
  };
}

/**
 * Browser-storage keys PostHog writes: the persistence blob
 * (`ph_phc_<token>_posthog`, localStorage and/or sessionStorage) and the
 * opt-in/out state (`__ph_opt_in_out_<token>`, localStorage). vanilla-
 * cookieconsent's autoClear only reaches cookies, so these are removed here.
 */
export const POSTHOG_STORAGE_KEY_PATTERNS: readonly RegExp[] = [
  /^ph_phc_.*_posthog$/,
  /^__ph_opt_in_out_/,
];
